import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, RefreshCw, AlertCircle, ArrowLeft, Settings } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Heading,
  MetricCard,
  PageHeader,
  Skeleton,
} from '@aaimplatform/ui';
import { useThirdPartyIntegrations, useTestIntegration } from '../../hooks/useIntegrations';
import type { Integration, IntegrationTestResult } from '../../api/client';
import { resolveProviderPanel, hasProviderPanel, resolveProviderLink } from './providers';
import type { ProviderRegistryEntry } from './providers';
import { INTEGRATION_STATUS_BADGE } from './integration-status';
import { ProviderMark } from './provider-mark';
import { formatDateTime } from '../../utils/display-format';

/** What a card says when this console has nowhere to configure the provider. */
export const NO_PANEL_TEXT = 'No configuration panel in this console';

/** The page description. It promises no panel the grid cannot reach. */
export const INTEGRATIONS_DESCRIPTION =
  'Third-party service connections: status, the last sync, and a connection test for a connected provider. A provider with a configuration page in this console links to it.';

/**
 * Integrations Configuration - Platform Admin
 *
 * Third-party service connections with composable provider-specific
 * detail panels loaded from the provider registry. A provider configured on a
 * page of its own (ATTOM) links there. A provider with neither says only that
 * this console has no configuration panel for it: whether it is configured
 * somewhere else is not something this page can know.
 */
export function IntegrationsConfigPage() {
  const { data: integrations, isLoading, error } = useThirdPartyIntegrations();
  const testMutation = useTestIntegration();

  // Provider detail panel state
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<ProviderRegistryEntry | null>(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, Omit<IntegrationTestResult, 'details'> | { failed: string }>>({});

  const handleTest = (id: string) => {
    testMutation.mutate(id, {
      onSuccess: (result) => setTestResults(prev => ({ ...prev, [id]: result })),
      onError: (err) => setTestResults(prev => ({ ...prev, [id]: { failed: err.message } })),
    });
  };

  const handleConfigure = async (integration: Integration) => {
    const providerKey = integration.name;
    setPanelLoading(true);
    try {
      const panel = await resolveProviderPanel(providerKey);
      if (panel) {
        setSelectedIntegrationId(integration.id);
        setActivePanel(panel);
      }
    } finally {
      setPanelLoading(false);
    }
  };

  const handleBackToGrid = () => {
    setSelectedIntegrationId(null);
    setActivePanel(null);
  };

  if (error) {
    return (
      <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-error flex-shrink-0" aria-hidden="true" />
        <div className="text-sm font-sans text-error">
          Failed to load integrations: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <div className="mb-6">
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  // Provider detail panel view
  if (activePanel && selectedIntegrationId) {
    const PanelComponent = activePanel.component;
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={handleBackToGrid} aria-label="Back to integrations">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          </Button>
          <div>
            <Heading level={3}>
              {activePanel.displayName}
            </Heading>
            <p className="text-xs font-sans text-tertiary leading-normal">
              {activePanel.description}
            </p>
          </div>
        </div>
        <PanelComponent
          integrationId={selectedIntegrationId}
          onBack={handleBackToGrid}
        />
      </div>
    );
  }

  const items = integrations ?? [];
  const countOf = (status: Integration['status']) => items.filter(i => i.status === status).length;

  return (
    <div>
      <PageHeader description={INTEGRATIONS_DESCRIPTION} />

      {/* Integrations Grid */}
      {items.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-sm font-sans text-tertiary">No integrations configured</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {items.map(integration => {
            const isTesting = testMutation.isPending && testMutation.variables === integration.id;
            const hasPanel = hasProviderPanel(integration.name);
            const pageLink = hasPanel ? null : resolveProviderLink(integration);
            const badge = INTEGRATION_STATUS_BADGE[integration.status];
            const lastSync = integration.lastSyncAt ? formatDateTime(integration.lastSyncAt) : null;
            const testResult = testResults[integration.id];
            const actionLabel =
              integration.status === 'NOT_CONFIGURED'
                ? 'Connect'
                : integration.status === 'ERROR'
                  ? 'Fix'
                  : 'Configure';
            return (
              <Card key={integration.id}>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <ProviderMark name={integration.name} />
                    <div className="min-w-0">
                      <Heading level={2} as={3} size="card">
                        {integration.name}
                      </Heading>
                      <p className="text-xs font-sans text-tertiary leading-normal">
                        {integration.description}
                      </p>
                    </div>
                  </div>
                  <span className="flex-shrink-0 whitespace-nowrap">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </span>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-recessed">
                  <div className="text-xs font-sans text-tertiary leading-normal">
                    {isTesting ? (
                      'Testing connection...'
                    ) : testResult && 'failed' in testResult ? (
                      <span className="text-error">Connection test failed: {testResult.failed}</span>
                    ) : testResult ? (
                      testResult.success ? (
                        `Connection test passed (${testResult.latencyMs} ms)`
                      ) : (
                        <span className="text-error">
                          Connection test failed{testResult.error ? `: ${testResult.error}` : ''}
                        </span>
                      )
                    ) : (
                      `Last sync: ${lastSync ?? 'Not recorded'}`
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {integration.status === 'CONNECTED' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTest(integration.id)}
                        disabled={isTesting}
                        aria-label={`Test ${integration.name} connection`}
                      >
                        <RefreshCw
                          className={`w-4 h-4 ${isTesting ? 'animate-spin' : ''}`}
                          aria-hidden="true"
                        />
                      </Button>
                    )}
                    {integration.docsUrl && (
                      <a
                        href={integration.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${integration.name} documentation (opens in a new tab)`}
                        className="p-2 rounded-sm text-secondary hover:bg-elevated transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" aria-hidden="true" />
                      </a>
                    )}
                    {hasPanel ? (
                      <Button
                        variant={integration.status === 'NOT_CONFIGURED' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => void handleConfigure(integration)}
                        disabled={panelLoading}
                      >
                        <span className="flex items-center gap-1">
                          {integration.status !== 'NOT_CONFIGURED' && (
                            <Settings className="w-3 h-3" aria-hidden="true" />
                          )}
                          {actionLabel}
                        </span>
                      </Button>
                    ) : pageLink ? (
                      <Link
                        to={pageLink.href}
                        className="text-xs font-sans font-medium text-accent hover:underline"
                      >
                        {pageLink.label}
                      </Link>
                    ) : (
                      <span className="text-xs font-sans text-tertiary">{NO_PANEL_TEXT}</span>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Integration Health Summary */}
      <div className="mt-6">
        <Heading level={2} as={3} className="mb-4">
          Integration Health
        </Heading>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label="Connected" value={String(countOf('CONNECTED'))} />
          <MetricCard label="Configured" value={String(countOf('CONFIGURED'))} />
          <MetricCard label="Errors" value={String(countOf('ERROR'))} />
          <MetricCard label="Not configured" value={String(countOf('NOT_CONFIGURED'))} />
        </div>
      </div>
    </div>
  );
}
